import { type VoidFunctionComponent } from 'react';

import useGetMalwareInfo from '../../../../hooks/api/malware/useGetMalwareInfo';
import { Link } from '../../../generic';
import DetailsPanel from '../../../generic/DetailsPanel';
import Loader from '../../../generic/Loader';
import Section from '../../../shared/vulns/Drawer/Section';

import styles from '../IssueContent.module.less';

type MalwareDescriptionProps = {
  malwareRevisionId: number;
};

/**
 * Displays malware information wrapped in a Section/DetailsPanel.
 * Shows the malware report name, description, and a link to the report URL.
 */
const MalwareDescription: VoidFunctionComponent<MalwareDescriptionProps> = ({
  malwareRevisionId,
}) => {
  const { data: malwareInfo, isLoading, isError } = useGetMalwareInfo(malwareRevisionId);

  if (isLoading) {
    return <Loader />;
  }

  let body;
  if (isError) {
    // Distinguish a failed fetch from a dependency that genuinely has no report, so the user
    // gets an actionable next step instead of a misleading "no details available" message.
    body = (
      <div>
        We couldn&apos;t load the malware report details. Please refresh the page to try again.
      </div>
    );
  } else if (!malwareInfo) {
    body = (
      <div>
        This dependency has been flagged as malicious, but additional details are not available at this time.
      </div>
    );
  } else {
    body = (
      <div className={styles.issueDetails}>
        <div className="padding-top-xs">
          <strong>{malwareInfo.name}</strong>
        </div>

        <div>
          {malwareInfo.description}
        </div>

        <div className="padding-top-md">
          <strong>Read more:</strong>&nbsp;
          <Link to={malwareInfo.url} variant="text" openInNewTab>
            {malwareInfo.url}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <Section>
      <DetailsPanel id="issue-details-malware" target="Malware details">
        {body}
      </DetailsPanel>
    </Section>
  );
};

export default MalwareDescription;
